import { useState, useEffect, useCallback } from 'react';
import { notificationsService } from '../api/services/notifications.service';
import {
  NotificationDto,
  NotificationType,
  GetNotificationsOptions,
} from '../api/types/notification.types';

export interface UseNotificationsOptions {
  type?: NotificationType;
  isRead?: boolean;
  page?: number;
  limit?: number;
  autoFetch?: boolean;
}

export interface UseNotificationsReturn {
  notifications: NotificationDto[];
  total: number;
  page: number;
  totalPages: number;
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  setPage: (page: number) => void;
  setType: (type: NotificationType | undefined) => void;
}

export function useNotifications(
  options: UseNotificationsOptions = {},
): UseNotificationsReturn {
  const { autoFetch = true } = options;

  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(options.page || 1);
  const [totalPages, setTotalPages] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [type, setType] = useState<NotificationType | undefined>(options.type);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const fetchOptions: GetNotificationsOptions = {
        page,
        limit: options.limit || 50,
      };

      if (type) {
        fetchOptions.type = type;
      }
      if (options.isRead !== undefined) {
        fetchOptions.isRead = options.isRead;
      }

      const response = await notificationsService.getNotifications(fetchOptions);

      setNotifications(response.notifications);
      setTotal(response.total);
      setTotalPages(response.totalPages);
      setUnreadCount(response.unreadCount);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch notifications'));
    } finally {
      setIsLoading(false);
    }
  }, [page, type, options.limit, options.isRead]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationsService.markAsRead(notificationId);
      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to mark notification as read'));
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsService.markAllAsRead();
      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() })),
      );
      setUnreadCount(0);
      // Dispatch event to notify other components (e.g., NotificationBell)
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to mark all as read'));
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchNotifications();
    }
  }, [autoFetch, fetchNotifications]);

  return {
    notifications,
    total,
    page,
    totalPages,
    unreadCount,
    isLoading,
    error,
    refetch: fetchNotifications,
    markAsRead,
    markAllAsRead,
    setPage,
    setType,
  };
}

export interface UseNotificationPollingOptions {
  interval?: number; // milliseconds, default 30000 (30 seconds)
  enabled?: boolean;
}

export interface UseNotificationPollingReturn {
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useNotificationPolling(
  options: UseNotificationPollingOptions = {},
): UseNotificationPollingReturn {
  const { interval = 30000, enabled = true } = options;

  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    // Skip if document is hidden
    if (document.hidden) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const count = await notificationsService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch unread count'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Initial fetch
    fetchUnreadCount();

    // Set up polling interval
    const intervalId = setInterval(fetchUnreadCount, interval);

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUnreadCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for notifications-updated event (e.g., from markAllAsRead)
    const handleNotificationsUpdated = () => {
      fetchUnreadCount();
    };
    window.addEventListener('notifications-updated', handleNotificationsUpdated);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('notifications-updated', handleNotificationsUpdated);
    };
  }, [enabled, interval, fetchUnreadCount]);

  return {
    unreadCount,
    isLoading,
    error,
    refetch: fetchUnreadCount,
  };
}
